// used specimen public page, including
// 1. CommentsCard , after specimen has been published, to post comments and to show everybody's comments,
// 2. reviewCommentsCard, after specimen is assigned to a reviewed, only accessible to the author and to the reviewer of the specimen
import { gql, useMutation } from '@apollo/client';
import Editor from 'components/TxtEditor/Editor';
import CustomSpinner from 'components/common/CustomSpinner';
import ThumbnailImage from 'components/custom/ThumbnailImage';
import ThumbnailLetters from 'components/custom/ThumbnailLetters';
import { UserContext } from 'contexts/user/UserContextProvider';
import { Field, Formik, FormikValues } from 'formik';
import IntlMessages from 'helpers/IntlMessages';
import FormattedEventCourseTime from 'pages/EventCourse/components/FormattedEventCourseTime';
import { useCallback, useContext, useState } from 'react';
import { Badge, Button, Card, Form } from 'react-bootstrap';
import HtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import ShowMoreText from 'react-show-more-text';
import {
  SpeciesSpecimenComment,
  SpeciesSpecimenReviewComment,
  Speciman,
  User,
} from 'services/graphqltypes';
import * as Yup from 'yup';


const CREATE_REVIEW_COMMENT = gql`
mutation createSpecimenReviewComment($content: String!, $specimenId: UUID!, $slug: String!) {
  createSpeciesSpecimenReviewComment(
    input: {
      speciesSpecimenReviewComment: {
        content: $content
        specimenId: $specimenId
      }
    }
  ) {
    query {
      specimanBySlug(slug: $slug) {
        speciesSpecimenReviewCommentsBySpecimenId(orderBy: CREATED_AT_DESC) {
          nodes {
            id
            createdAt
            content
            createdByUser {
              avatarUrl
              name
              username
              isTerritoryMentor
            }
          }
        }
      }
    }
  }
}`;

const CREATE_COMMENT = gql`
mutation createSpecimenComment($content: String!, $specimenId: UUID!, $slug: String!) {
  createSpeciesSpecimenComment(
    input: {
      speciesSpecimenComment: {
        content: $content
        specimenId: $specimenId
      }
    }
  ) {
    query {
      specimanBySlug(slug: $slug) {
        speciesSpecimenCommentsBySpecimenId(orderBy: CREATED_AT_DESC) {
          nodes {
            id
            createdAt
            content
            createdByUser {
              avatarUrl
              name
              username
              isTerritoryMentor
            }
          }
        }
      }
    }
  }
}`;


const ProfilePicWtMentorBadge = ({ user }: { user: User }) => {
  return (
    < div >
      {/* user avatar */}
      {user?.avatarUrl ? (
        <ThumbnailImage rounded small src={user.avatarUrl} alt="Profile picture" className={user.isTerritoryMentor ? 'mr-2' : 'mr-3'} />
      ) : (
        <ThumbnailLetters rounded small text={user.name} className={user.isTerritoryMentor ? 'mr-2' : 'mr-3'} />
      )
      }
      {/* mentor badge */}
      {user.isTerritoryMentor &&
        <Badge
          className={'position-relative badge-bottom-right-user-card pl-0 pt-0 pb-0'}
        >
          <ThumbnailImage
            rounded
            src='/assets/img/profiles/commons-mentor-badge.png'
            alt="Commons mentor badge"
            className="mr-2 img-thumbnail-badge position-absolute "
            style={{ border: '1px solid #dee2e6', borderRadius: '.25rem' }}
          />
        </Badge>
      }
    </div>
  );
};

const SingleComment = ({ comment, showSeparator = true }:
  { comment: SpeciesSpecimenReviewComment | SpeciesSpecimenComment, showSeparator?: boolean }) => {
  return (
    <Card.Body className='py-0 px-1'>
      <div>
        <Link to={`/user/${comment.createdByUser?.username}`}>
          <div className="d-flex flex-row mb-3">
            <ProfilePicWtMentorBadge user={comment?.createdByUser as User} />
            { /* username, comment date */}
            <div className="d-flex flex-grow-1 align-self-center">
              <div className="pl-0 align-self-center d-flex flex-column  justify-content-between min-width-zero">
                <div className="mb-0 pb-0 h5">{comment.createdByUser?.name}</div>
                <div className="text-muted text-normal pt-0 mb-0">
                  <FormattedEventCourseTime startTime={comment.createdAt} />
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div >

      {/* {comment.content} */}
      < div className='mb-3' >
        <ShowMoreText
          lines={3}
          more={<div className='d-flex flex-row-reverse mt-2 mb-2'> Show more </div>}
          less={<div className='d-flex flex-row-reverse mt-2 mb-2'> Show less </div>}
          className='show-more-text'
          expanded={false}
        >
          <div className="text-normal html-parser" style={{ lineBreak: 'auto', }}>
            {HtmlParser(comment.content)}
          </div>
        </ShowMoreText>
      </div >

      {/* separator */}
      {showSeparator && < div className="border-bottom mb-4" />}
    </Card.Body >


  );
};


// Comments card (for everyone's posts, after specimen has been published)
export const CommentsCard = ({ speciman }: { speciman: Speciman }) => {
  const intl = useIntl();
  const { isAuthenticated, currentUser } = useContext(UserContext);
  const [submitting, setSubmitting] = useState(false); // we have to have this because quill doesn't rerender when formik value changes, I can't delete the content
  const [createComment] = useMutation(CREATE_COMMENT);
  const [clickedSubmit, setClickedSubmit] = useState(false);

  const specimanComments = speciman.speciesSpecimenCommentsBySpecimenId?.nodes; // shorter name

  /* validation schema */
  const validationSchema = Yup.object().shape(
    {
      comment: Yup.string().required(intl.formatMessage({ id: 'general.required' })).min(12, intl.formatMessage({ id: 'general.too-short' })), // html editor by default has some characters
    });

  /* submit form (comment)) */
  const handleSubmit = useCallback((values: FormikValues) => {
    setSubmitting(true); // to force show the "Loading" so that whole thing rerenders and quill is empty
    createComment({
      variables: {
        content: values.comment,
        specimenId: speciman.id,
        slug: speciman.slug,
      }
    }).then(() => {
      setSubmitting(false);
    });
  }, [createComment, speciman]);

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="mb-4">
          <span className="text-primary h4">
            {intl.formatMessage({ id: 'general.comments' })}
            {specimanComments.length > 0 &&
              ` (${specimanComments.length})`}
          </span>
        </Card.Title>

        { /* new comment editor: only show to logged-in users */}
        {
          isAuthenticated ?
            <Card.Body className='px-1 py-0'>
              { /* profile pic */}
              <div>
                <Link to={`/user/${currentUser.username}`}>
                  <div className="d-flex flex-row mb-3">
                    <ProfilePicWtMentorBadge user={currentUser} />
                    { /* name */}
                    <div className="d-flex flex-grow-1 align-self-center">
                      <div className="pl-0 align-self-center d-flex flex-column  justify-content-between min-width-zero">
                        <div className="mb-0 pb-0 h5">{currentUser.name}</div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              { /* the comment content (html text) */}
              <div className='mb-4'>
                {submitting ?
                  // loading spinner
                  <div className="mt-2">
                    <div className="d-flex justify-content-center">
                      <div className="loading-relative-position mt-2 mb-2" />
                    </div>
                  </div>
                  :
                  <Formik
                    initialValues={{ 'comment': '' }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                      handleSubmit(values);
                    }}
                  >
                    {({ errors, handleSubmit, isValid, values, handleBlur, isSubmitting }) => (
                      <Form className="av-tooltip tooltip-label-right" onSubmit={handleSubmit}>

                        { // validation error message
                          clickedSubmit && errors.comment &&
                          <div className="form-field-error" style={{ 'color': 'red' }}>{errors.comment}</div>
                        }
                        <Field
                          name="comment"
                          className='form-control-editor'
                          component={Editor}
                          value={values.comment}
                          handleBlur={handleBlur('comment')} // makes handleBlur work with our Editor component and mark the field as touched properly
                        />
                        { /* Post button (submit) */}
                        <div className="text-right">
                          <Button
                            className="ml-auto mt-2 align-self-end btn btn-yellow btn-lg"
                            type='submit'
                            disabled={isSubmitting || (clickedSubmit && !isValid)}
                            onClick={() => { !isSubmitting && setClickedSubmit(true); }}>
                            {isSubmitting ?
                              <CustomSpinner />
                              :
                              <IntlMessages id="Post" />}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                }
              </div>
              {/* separator */}
              { /* specimanComments.length > 0 && <div className="border-bottom mb-4" /> I think it looks better without*/}
            </Card.Body>
            :
            <div>
              { /* otherwise show CTA: Log in to be able to comment */}
              <Card.Body>
                <Card.Text className="text-center text-normal">
                  <span>To post a comment, please </span><span className="text-normal h4"><Link className='text-primary' to='/login'>log in</Link>.</span>
                </Card.Text>
              </Card.Body>
            </div>
        }

        { /* render comments */}
        <div className="mt-3">
          {specimanComments.map((
            comment: SpeciesSpecimenComment, index: number) =>
            <SingleComment
              key={index}
              comment={comment}
              // don't show separator for last comment
              showSeparator={
                index < (specimanComments.length - 1)}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export const ReviewCommentsCard = ({ speciman }: { speciman: Speciman }) => {
  const intl = useIntl();
  const { currentUser } = useContext(UserContext);
  const [submitting, setSubmitting] = useState(false); // we have to have this because quill doesn't rerender when formik value changes, I can't delete the content
  const [createReviewComment] = useMutation(CREATE_REVIEW_COMMENT);
  const [clickedSubmit, setClickedSubmit] = useState(false);

  const specimanReviewComments = speciman.speciesSpecimenReviewCommentsBySpecimenId?.nodes; // shorter name
  /* validation schema */
  const validationSchema = Yup.object().shape(
    {
      comment: Yup.string().required(intl.formatMessage({ id: 'general.required' })).min(12, intl.formatMessage({ id: 'general.too-short' })), // html editor by default has some characters
    });

  /* submit form (comment)) */
  const handleSubmit = useCallback((values: FormikValues) => {
    setSubmitting(true); // to force show the "Loading" so that whole thing rerenders and quill is empty
    createReviewComment({
      variables: {
        content: values.comment,
        specimenId: speciman.id,
        slug: speciman.slug,
      }
    }).then(() => {
      setSubmitting(false);
    });
  }, [createReviewComment, speciman]);

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="mb-4">
          <span className="text-primary h4">
            {intl.formatMessage({ id: 'bioregion.specimen.review-comments' })}
            {specimanReviewComments.length > 0 &&
              ` (${specimanReviewComments.length})`}
          </span>
        </Card.Title>

        { /* new comment editor: only show to the author and to the reviewer */}
        {
          (speciman.reviewerId || speciman.createdByUserId === currentUser?.id) ?
            <Card.Body className='px-1 py-0'>
              { /* profile pic */}
              <div>
                <Link to={`/user/${currentUser.username}`}>
                  <div className="d-flex flex-row mb-3">
                    <ProfilePicWtMentorBadge user={currentUser} />
                    { /* name */}
                    <div className="d-flex flex-grow-1 align-self-center">
                      <div className="pl-0 align-self-center d-flex flex-column  justify-content-between min-width-zero">
                        <div className="mb-0 pb-0 h5">{currentUser.name}</div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              { /* the comment content (html text) */}
              <div className='mb-4'>
                {submitting ?
                  // loading spinner
                  <div className="mt-2">
                    <div className="d-flex justify-content-center">
                      <div className="loading-relative-position mt-2 mb-2" />
                    </div>
                  </div>
                  :
                  <Formik
                    initialValues={{ 'comment': '' }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                      handleSubmit(values);
                    }}
                  >
                    {({ errors, handleSubmit, isValid, values, handleBlur }) => (
                      <Form className="av-tooltip tooltip-label-right" onSubmit={handleSubmit}>

                        { // validation error message
                          clickedSubmit && errors.comment &&
                          <div className="form-field-error" style={{ 'color': 'red' }}>{errors.comment}</div>
                        }
                        <Field
                          name="comment"
                          className='form-control-editor'
                          component={Editor}
                          value={values.comment}
                          handleBlur={handleBlur('comment')} // makes handleBlur work with our Editor component and mark the field as touched properly
                        />
                        { /* Post button (submit) */}
                        <div className="text-right">
                          <Button
                            className="ml-auto mt-2 align-self-end btn btn-yellow btn-lg"
                            disabled={(!clickedSubmit ? false : !isValid)}
                            type="submit"
                            onClick={() => {
                              setClickedSubmit(true);
                            }
                            }
                          >
                            <IntlMessages id="Post" />
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                }
              </div>
              {/* separator */}
              { /* specimanReviewComments.length > 0 && < div className="border-bottom mb-4" /> */}
            </Card.Body>
            :
            <div>
              { /* otherwise show CTA: Become reviewer to be able to comment */}
              <Card.Body>
                <Card.Text>
                  <span className="text-normal h4">To participate in the review discussion, you have to <b>become the reviewer</b> first.</span>
                </Card.Text>
              </Card.Body>
            </div>
        }

        { /* render comments */}
        <div className="mt-3">
          {specimanReviewComments.map((
            comment: SpeciesSpecimenReviewComment, index: number) =>
            <SingleComment
              key={index}
              comment={comment}
              // don't show separator for last comment
              showSeparator={
                index < (specimanReviewComments.length - 1)}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

